<template>
  <div class="set-confirm-step wizard-step">
    <!-- C案: 左 (時間 + アイテム編集) / 右 (サマリー固定) -->
    <div class="confirm-layout">
      <!-- 左カラム: 操作領域 -->
      <div class="confirm-main">
        <!-- 料金確認カード（内部スクロール、セット・指名・チャージの個別税サを管理） -->
        <section class="wizard-section-card wizard-section-card--fill">
          <div class="wizard-section-header">
            <span class="wizard-section-title">料金・税サ</span>
            <v-spacer />
            <v-btn
              text
              small
              color="primary"
              @click="toggleMode"
            >
              {{ isIndividual ? 'まとめて設定する' : '個別に設定する' }}
            </v-btn>
          </div>

          <div class="wizard-section-scroll">
            <!-- セット -->
            <div class="item-group">
              <div class="item-group-title">
                セット
              </div>
              <!-- bulk モード -->
              <div
                v-if="!isIndividual"
                class="d-flex align-center px-4 py-4 item-row"
              >
                <div class="flex-grow-1 text-h6 font-weight-medium">
                  {{ customerCount }}名（まとめ）
                </div>
                <v-text-field
                  :value="set.price"
                  type="number"
                  hide-details
                  outlined
                  style="max-width: 140px;"
                  class="set-input"
                  @input="updateBulkPrice"
                />
                <span class="mx-2 text-body-1">円</span>
                <v-btn-toggle
                  :value="taxCharge || 'normal'"
                  mandatory
                  class="ml-2"
                  @change="updateBulkTaxCharge"
                >
                  <v-btn value="normal">
                    通常
                  </v-btn>
                  <v-btn
                    v-if="(taxCharge || 'normal') === 'included'"
                    value="included"
                  >
                    込み
                  </v-btn>
                  <v-btn value="cut">
                    カット
                  </v-btn>
                </v-btn-toggle>
              </div>

              <!-- individual モード -->
              <div
                v-else
                class="px-4 pb-3"
              >
                <set-editable-row
                  v-for="(row, idx) in set.perCustomer"
                  :key="`set-row-${idx}`"
                  :label="`お客様${idx + 1}`"
                  :price="row.price"
                  :tax-charge="row.taxCharge || 'normal'"
                  :start-at="row.startAt || startAt"
                  @update:price="updatePerCustomer(idx, { price: $event })"
                  @update:taxCharge="updatePerCustomer(idx, { taxCharge: $event })"
                  @update:startAt="updatePerCustomer(idx, { startAt: $event })"
                />
              </div>
            </div>

            <!-- 指名 -->
            <div
              v-if="nominations.length > 0"
              class="item-group"
            >
              <div class="item-group-title">
                指名
              </div>
              <div
                v-for="n in nominations"
                :key="`nom-${n.castId}-${n.vendibleId}`"
                class="d-flex align-center px-4 py-3 item-row"
              >
                <div class="flex-grow-1 text-body-1">
                  <span class="font-weight-bold">{{ n.castName }}</span>
                  <span class="text--secondary ml-3">{{ n.vendibleName }}</span>
                </div>
                <span class="text-body-1 font-weight-medium mr-4">
                  {{ formatPrice(n.sellingPrice) }} 円
                </span>
                <v-btn-toggle
                  :value="n.taxCharge || 'normal'"
                  mandatory
                  @change="onNominationTaxChange(n, $event)"
                >
                  <v-btn value="normal">
                    通常
                  </v-btn>
                  <v-btn
                    v-if="(n.taxCharge || 'normal') === 'included'"
                    value="included"
                  >
                    込み
                  </v-btn>
                  <v-btn value="cut">
                    カット
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>

            <!-- チャージ -->
            <div
              v-if="charges.length > 0"
              class="item-group"
            >
              <div class="item-group-title">
                チャージ
              </div>
              <div
                v-for="c in charges"
                :key="`chg-${c.vendibleId}`"
                class="d-flex align-center px-4 py-3 item-row"
              >
                <div class="flex-grow-1 text-body-1 font-weight-bold">
                  {{ c.name }}
                </div>
                <span class="text-body-1 font-weight-medium mr-4">
                  {{ formatPrice(c.sellingPrice) }} 円
                </span>
                <v-btn-toggle
                  :value="c.taxCharge || 'normal'"
                  mandatory
                  @change="onChargeTaxChange(c, $event)"
                >
                  <v-btn value="normal">
                    通常
                  </v-btn>
                  <v-btn
                    v-if="(c.taxCharge || 'normal') === 'included'"
                    value="included"
                  >
                    込み
                  </v-btn>
                  <v-btn value="cut">
                    カット
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- 右カラム: サマリー固定 -->
      <aside class="confirm-summary">
        <section class="wizard-section-card">
          <div class="wizard-section-header wizard-section-header--accent">
            <span class="wizard-section-title">合計</span>
          </div>

          <!-- 開始時間（席種・客数の画面で設定済み） -->
          <div class="summary-start-at">
            <div class="d-flex align-center text-caption text--secondary mb-1">
              <v-icon
                x-small
                class="mr-1"
              >
                {{ icons.mdiClockOutline }}
              </v-icon>
              <span>セット開始時間</span>
              <span class="ml-2">（席種・客数の画面で変更）</span>
            </div>
            <div class="text-h3 font-weight-bold primary--text text-center">
              {{ formatTime(startAt) }}
            </div>
          </div>

          <v-divider />

          <div class="pa-4">
            <div class="d-flex justify-space-between text-body-1 py-1">
              <span class="text--secondary">セット小計</span>
              <span>{{ formatPrice(subtotal) }} 円</span>
            </div>
            <div class="d-flex justify-space-between text-body-1 py-1">
              <span class="text--secondary">指名</span>
              <span>{{ formatPrice(nominationsTotal) }} 円</span>
            </div>
            <div class="d-flex justify-space-between text-body-1 py-1">
              <span class="text--secondary">チャージ</span>
              <span>{{ formatPrice(chargesTotal) }} 円</span>
            </div>
            <div class="d-flex justify-space-between text-body-1 py-1">
              <span class="text--secondary">税・サービス料</span>
              <span>{{ formatPrice(estimatedTax) }} 円</span>
            </div>

            <v-divider class="my-3" />

            <div class="d-flex justify-space-between align-center py-1">
              <span class="text-body-1 font-weight-medium">合計（税サ込み）</span>
              <span class="text-h4 font-weight-bold primary--text">{{ formatPrice(grandTotalWithTax) }} 円</span>
            </div>

            <div
              v-if="cutCount > 0"
              class="text-caption text--secondary text-right mt-1"
            >
              ※ {{ cutCount }} 件は税サ対象外（カット・込み）
            </div>
          </div>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from '@vue/composition-api'
import { mdiClockOutline } from '@mdi/js'
import CheckoutConfigApi from '@/api/waiter/CheckoutConfig'
import SetEditableRow from '../parts/SetEditableRow.vue'

const buildPerCustomer = (count, base) => {
  const arr = []
  for (let i = 0; i < count; i += 1) {
    arr.push({
      price: base.price,
      taxCharge: base.taxCharge || 'normal',
      startAt: base.startAt instanceof Date ? new Date(base.startAt.getTime()) : null,
    })
  }

  return arr
}

const pad2 = n => String(n).padStart(2, '0')

export default {
  name: 'SetConfirmStep',
  components: { SetEditableRow },
  props: {
    customerCount: { type: Number, required: true },
    selectedCourse: { type: Object, default: null },
    set: { type: Object, required: true },
    taxCharge: { type: String, default: 'normal' },
    startAt: { type: Date, default: null },
    nominations: { type: Array, default: () => [] },
    charges: { type: Array, default: () => [] },
    nominationsTotal: { type: Number, default: 0 },
    chargesTotal: { type: Number, default: 0 },
    checkoutConfig: { type: Object, default: () => ({}) },
  },
  emits: [
    'update:set', 'update:taxCharge', 'update:startAt',
    'update:nomination-tax', 'update:charge-tax',
  ],
  setup(props, { emit }) {
    const isIndividual = computed(() => props.set.mode === 'individual')

    const subtotal = computed(() => {
      if (isIndividual.value) {
        return (props.set.perCustomer || [])
          .reduce((sum, r) => sum + Number(r.price || 0), 0)
      }

      return Number(props.set.price || 0) * Math.max(1, props.customerCount)
    })

    const grandTotal = computed(() => (
      Number(subtotal.value || 0)
        + Number(props.nominationsTotal || 0)
        + Number(props.chargesTotal || 0)
    ))

    // 税サ計算: taxCharge='normal' のアイテムだけを対象に CheckoutConfig.calcTax を呼ぶ
    // 'cut'(カット) と 'included'(込み) はどちらも税サを加算しない
    const isTaxExemptOf = item => ['cut', 'included'].includes(item?.taxCharge || 'normal')

    const bulkSetNormal = () => {
      if (['cut', 'included'].includes(props.taxCharge || 'normal')) return 0

      return Number(props.set.price || 0) * Math.max(1, props.customerCount)
    }

    const taxableSubtotal = computed(() => {
      const setNormal = isIndividual.value
        ? (props.set.perCustomer || [])
          .filter(r => !isTaxExemptOf(r))
          .reduce((s, r) => s + Number(r.price || 0), 0)
        : bulkSetNormal()
      const nomNormal = (props.nominations || [])
        .filter(n => !isTaxExemptOf(n))
        .reduce((s, n) => s + Number(n.sellingPrice || 0), 0)
      const chargeNormal = (props.charges || [])
        .filter(c => !isTaxExemptOf(c))
        .reduce((s, c) => s + Number(c.sellingPrice || 0), 0)

      return setNormal + nomNormal + chargeNormal
    })

    const estimatedTax = ref(0)
    let calcTaxSeq = 0

    const fetchTax = async () => {
      const price = taxableSubtotal.value
      if (!price || !props.checkoutConfig || !props.checkoutConfig.taxMode) {
        estimatedTax.value = 0

        return
      }
      calcTaxSeq += 1
      const seq = calcTaxSeq
      try {
        const res = await CheckoutConfigApi.calcTax({
          price,
          serviceChargeRate: Number(props.checkoutConfig.serviceChargeRate) || 0,
          taxMode: props.checkoutConfig.taxMode || 'normal',
          taxRoundingMethod: props.checkoutConfig.taxRoundingMethod || 'round',
          taxRoundingDigit: props.checkoutConfig.taxRoundingDigit || 0,
          taxRoundingMultiple: props.checkoutConfig.taxRoundingMultiple || 0,
        })
        if (seq !== calcTaxSeq) return // 古いレスポンスを破棄
        const v = Number(res?.data?.tax ?? res?.data ?? 0)
        estimatedTax.value = Number.isFinite(v) ? v : 0
      } catch (_) {
        estimatedTax.value = 0
      }
    }

    let debounceTimer = null
    watch(
      () => [taxableSubtotal.value, props.checkoutConfig],
      () => {
        if (debounceTimer) clearTimeout(debounceTimer)
        debounceTimer = setTimeout(fetchTax, 200)
      },
      { immediate: true, deep: true },
    )

    const rawGrandTotalWithTax = computed(() => grandTotal.value + Number(estimatedTax.value || 0))

    // 合計（大計）の端数処理: 実卓の Overview/Order と同じく「小計+税額」を
    // 店舗の totalRounding 設定（CheckoutConfig#calc_total）で丸めて表示する
    const grandTotalWithTax = ref(0)
    let calcTotalSeq = 0

    const fetchTotal = async () => {
      const price = rawGrandTotalWithTax.value
      if (!price || price <= 0 || !props.checkoutConfig || !props.checkoutConfig.totalRoundingMethod) {
        grandTotalWithTax.value = Math.max(Number(price) || 0, 0)

        return
      }
      calcTotalSeq += 1
      const seq = calcTotalSeq
      try {
        const res = await CheckoutConfigApi.calcTotal({
          price,
          totalRoundingMethod: props.checkoutConfig.totalRoundingMethod || 'round',
          totalRoundingDigit: props.checkoutConfig.totalRoundingDigit || 0,
          totalRoundingMultiple: props.checkoutConfig.totalRoundingMultiple || 0,
        })
        if (seq !== calcTotalSeq) return // 古いレスポンスを破棄
        const v = Number(res?.data ?? 0)
        grandTotalWithTax.value = Number.isFinite(v) && v > 0 ? v : price
      } catch (_) {
        grandTotalWithTax.value = price
      }
    }

    let totalDebounceTimer = null
    watch(
      () => [rawGrandTotalWithTax.value, props.checkoutConfig],
      () => {
        if (totalDebounceTimer) clearTimeout(totalDebounceTimer)
        totalDebounceTimer = setTimeout(fetchTotal, 200)
      },
      { immediate: true, deep: true },
    )

    const bulkSetCuts = () => (
      ['cut', 'included'].includes(props.taxCharge || 'normal') ? Math.max(1, props.customerCount) : 0
    )

    const cutCount = computed(() => {
      const setCuts = isIndividual.value
        ? (props.set.perCustomer || []).filter(r => isTaxExemptOf(r)).length
        : bulkSetCuts()
      const nominationCuts = (props.nominations || []).filter(n => isTaxExemptOf(n)).length
      const chargeCuts = (props.charges || []).filter(c => isTaxExemptOf(c)).length

      return setCuts + nominationCuts + chargeCuts
    })

    const formatPrice = n => Number(n || 0).toLocaleString('ja-JP')

    const formatTime = d => {
      if (!(d instanceof Date)) return '--:--'

      return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
    }

    const emitSet = next => emit('update:set', next)

    const toggleMode = () => {
      if (isIndividual.value) {
        emitSet({ ...props.set, mode: 'bulk' })
      } else {
        emitSet({
          ...props.set,
          mode: 'individual',
          perCustomer: buildPerCustomer(props.customerCount, {
            price: props.set.price,
            taxCharge: props.taxCharge || 'normal',
            startAt: props.startAt,
          }),
        })
      }
    }

    const updateBulkPrice = v => emitSet({ ...props.set, price: Number(v) || 0 })
    const updateBulkTaxCharge = v => emit('update:taxCharge', v || 'normal')

    const updatePerCustomer = (idx, patch) => {
      const next = (props.set.perCustomer || []).map((r, i) => (
        i === idx ? { ...r, ...patch } : r
      ))
      emitSet({ ...props.set, perCustomer: next })
    }

    const onNominationTaxChange = (n, v) => {
      emit('update:nomination-tax', {
        castId: n.castId,
        vendibleId: n.vendibleId,
        taxCharge: v || 'normal',
      })
    }

    const onChargeTaxChange = (c, v) => {
      emit('update:charge-tax', {
        vendibleId: c.vendibleId,
        taxCharge: v || 'normal',
      })
    }

    return {
      isIndividual,
      subtotal,
      grandTotal,
      grandTotalWithTax,
      estimatedTax,
      cutCount,
      formatPrice,
      formatTime,
      toggleMode,
      updateBulkPrice,
      updateBulkTaxCharge,
      updatePerCustomer,
      onNominationTaxChange,
      onChargeTaxChange,
      icons: { mdiClockOutline },
    }
  },
}
</script>

<style scoped>
/* C 案 2 カラム: 左 = 操作領域 / 右 = 固定サマリー */
.confirm-layout {
  display: flex;
  gap: 24px;
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
}
.confirm-main {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.confirm-summary {
  flex: 0 0 360px;
  align-self: stretch;
}
.set-input :deep(input) {
  font-size: 1.05rem;
  text-align: right;
}

/* サマリーカード先頭の「セット開始時間」エリア */
.summary-start-at {
  padding: 14px 16px 16px;
}

/* 料金カード内のグループ見出し (セット/指名/チャージ) */
.item-group + .item-group {
  border-top: 1px solid rgba(127, 127, 127, 0.12);
}
.item-group-title {
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: currentColor;
  padding: 10px 16px 6px;
  background: transparent;
  border-bottom: 1px solid rgba(127, 127, 127, 0.18);
}
.theme--light .item-group-title {
  opacity: 0.65;
}
.theme--dark .item-group-title {
  opacity: 0.9;
}
.item-row + .item-row {
  border-top: 1px dashed rgba(127, 127, 127, 0.18);
}
.set-input :deep(.v-input__slot) {
  min-height: 44px !important;
}

/* タブレット縦持ち等の狭い幅では 1 カラム */
@media (max-width: 768px) {
  .confirm-layout {
    flex-direction: column;
  }
  .confirm-summary {
    flex: 1 1 auto;
    width: 100%;
  }
}
</style>
